import { useEffect, useMemo, useRef } from 'react';
import { getOpenWorkOrderCount } from '../listHelpers';

export function useOccurrenceContext({
  templateOccurrences,
  selectedOccurrenceId,
  activeGroups,
  activeItems,
  activeTemplates,
}: {
  templateOccurrences: any[];
  selectedOccurrenceId: number | null;
  activeGroups: any[];
  activeItems: any[];
  activeTemplates: any[];
}) {
  const liveOccurrences = useMemo(
    () => (templateOccurrences || []).filter((occurrence: any) => !occurrence?.archivedAt),
    [templateOccurrences]
  );

  const occurrenceIdByTemplateId = useMemo(() => {
    const byTemplateId = new Map<number, number>();
    liveOccurrences.forEach((occurrence: any) =>
      byTemplateId.set(Number(occurrence.assetTemplateId), Number(occurrence.id))
    );
    return byTemplateId;
  }, [liveOccurrences]);

  const occurrenceIdByTemplateIdRef = useRef(occurrenceIdByTemplateId);
  useEffect(() => {
    occurrenceIdByTemplateIdRef.current = occurrenceIdByTemplateId;
  }, [occurrenceIdByTemplateId]);

  // Status is per-site, so it comes off the occurrence rather than the template.
  const statusByTemplateId = useMemo(() => {
    const byTemplateId = new Map<number, any>();
    liveOccurrences.forEach((occurrence: any) =>
      byTemplateId.set(Number(occurrence.assetTemplateId), occurrence.status)
    );
    return byTemplateId;
  }, [liveOccurrences]);

  const workOrdersByTemplateId = useMemo(() => {
    const byTemplateId = new Map<number, number>();
    liveOccurrences.forEach((occurrence: any) => {
      const liveGroups = new Set((occurrence.groupOccurrences || []).map((g: any) => Number(g.id)));
      const liveItems = new Set((occurrence.itemOccurrences || []).map((i: any) => Number(i.id)));
      const visible = (occurrence.taskOccurrences || []).filter((task: any) => {
        if (task?.assetItemOccurrenceId) return liveItems.has(Number(task.assetItemOccurrenceId));
        if (task?.assetGroupOccurrenceId) {
          return liveGroups.has(Number(task.assetGroupOccurrenceId));
        }
        return true;
      });
      byTemplateId.set(
        Number(occurrence.assetTemplateId),
        getOpenWorkOrderCount({ taskOccurrences: visible })
      );
    });
    return byTemplateId;
  }, [liveOccurrences]);

  const deployedTemplateIds = useMemo(
    () => liveOccurrences.map((occurrence: any) => Number(occurrence.assetTemplateId)),
    [liveOccurrences]
  );

  const openOccurrence = useMemo(
    () =>
      (templateOccurrences || []).find(
        (occurrence: any) => Number(occurrence.id) === Number(selectedOccurrenceId)
      ) || null,
    [templateOccurrences, selectedOccurrenceId]
  );

  const occurrenceGroups = useMemo(() => {
    const byId = new Map<number, any>(activeGroups.map((g: any) => [Number(g.id), g]));
    return (openOccurrence?.groupOccurrences || [])
      .filter((g: any) => !g?.archivedAt)
      .map((g: any) => ({
        ...(byId.get(Number(g.assetGroupId)) || g.assetGroup || {}),
        id: Number(g.assetGroupId),
        occurrenceId: Number(g.id),
        name: g.name || g.assetGroup?.name || byId.get(Number(g.assetGroupId))?.name,
        notes: g.notes ?? g.assetGroup?.notes ?? null,
      }));
  }, [openOccurrence, activeGroups]);

  const occurrenceItems = useMemo(() => {
    const byId = new Map<number, any>(activeItems.map((i: any) => [Number(i.id), i]));
    return (openOccurrence?.itemOccurrences || [])
      .filter((i: any) => !i?.archivedAt)
      .map((i: any) => ({
        ...(byId.get(Number(i.assetItemId)) || i.assetItem || {}),
        id: Number(i.assetItemId),
        occurrenceId: Number(i.id),
        name: i.name || i.assetItem?.name || byId.get(Number(i.assetItemId))?.name,
        notes: i.notes ?? i.assetItem?.notes ?? null,
      }));
  }, [openOccurrence, activeItems]);

  const groupOccurrenceIdByGroupId = useMemo(() => {
    const byGroupId = new Map<number, number>();
    (openOccurrence?.groupOccurrences || [])
      .filter((g: any) => !g?.archivedAt)
      .forEach((g: any) => byGroupId.set(Number(g.assetGroupId), Number(g.id)));
    return byGroupId;
  }, [openOccurrence]);

  const itemOccurrenceIdByItemId = useMemo(() => {
    const byItemId = new Map<number, number>();
    (openOccurrence?.itemOccurrences || [])
      .filter((i: any) => !i?.archivedAt)
      .forEach((i: any) => byItemId.set(Number(i.assetItemId), Number(i.id)));
    return byItemId;
  }, [openOccurrence]);

  const occurrenceTemplates = useMemo(
    () => activeTemplates.filter((t: any) => deployedTemplateIds.includes(Number(t.id))),
    [activeTemplates, deployedTemplateIds]
  );

  return {
    occurrenceIdByTemplateId,
    occurrenceIdByTemplateIdRef,
    statusByTemplateId,
    workOrdersByTemplateId,
    deployedTemplateIds,
    openOccurrence,
    occurrenceGroups,
    occurrenceItems,
    occurrenceTemplates,
    groupOccurrenceIdByGroupId,
    itemOccurrenceIdByItemId,
  };
}
